// Rendus Search des personnalités (Réf A standard / B artiste / C immersif présidentiel)
// + familles de résultats par intention (Réf F Coco). Données : provenance éditoriale marquée.
import { useNavigate } from "react-router-dom";
import { ArrowRight, Phone, Share2, Heart, Bookmark, BadgeCheck, Star, MapPin, GraduationCap, Briefcase, Image as ImgIcon, PlayCircle, Mic2, FileText, HandHeart, BookOpen, Music2, CalendarDays, Camera, Link2, Users, Globe2 } from "lucide-react";
import { toast } from "sonner";
import "./persona.css";

const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202f/g, " ")} F`;
const share = async (title, url) => {
  if (navigator.share) { try { await navigator.share({ title, url }); } catch { /* annulé */ } }
  else { try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); } catch { toast.error("Copie impossible"); } }
};

export function PersonalitySearchResult({ p }) {
  const navigate = useNavigate();
  const openFiche = () => navigate(`/personnalite/${p.slug}`);
  const url = () => `${window.location.origin}/personnalite/${p.slug}`;

  if (p.role_type === "presidential") {
    const SIDE_ICONS = { calendar: CalendarDays, heart: Heart, users: Users, globe: Globe2 };
    const PTABS = ["Aperçu", "Biographie", "Engagements", "Fondation", "Actions", "Actualités", "Photos", "Vidéos", "Discours", "Documents"];
    return (
      <div data-testid={`personality-immersive-${p.slug}`}>
        <div className="pr-hero">
          <img className="bg" src={p.cover} alt={p.name} />
          <div className="ovl" />
          <div className="inner">
            <span className="pr-badge orange">👑 {p.org_badge}</span>
            <h2>{p.name}</h2>
            <div className="role">{p.title}</div>
            <div className="quote">« {p.quote} »</div>
            <div className="acts">
              <button className="pr-cta" onClick={openFiche} data-testid={`personality-fiche-btn-${p.slug}`}>📷 Voir la fiche complète <ArrowRight size={15} /></button>
              <span className="pi-rounds">
                <button className="pi-round" onClick={() => toast.success("Personnalité enregistrée")} data-testid="pi-save"><Bookmark size={15} /><span>Enregistrer</span></button>
                <button className="pi-round" onClick={() => share(p.name, url())} data-testid="pi-share"><Share2 size={15} /><span>Partager</span></button>
                <button className="pi-round" onClick={() => toast.info("Source officielle — fiche AYA ID")} data-testid="pi-source"><Link2 size={15} /><span>Source officielle</span></button>
              </span>
            </div>
          </div>
        </div>

        <div className="pi-tabs" data-testid="pi-tabs">
          {PTABS.map((t, i) => (
            <button key={t} className={`pi-tab ${i === 0 ? "on" : ""}`} onClick={openFiche}>{t}</button>
          ))}
        </div>

        <div className="pi-grid">
          <div>
            <div className="pr-sechead"><h3>À propos</h3></div>
            <p className="pi-about" data-testid="pi-about">{p.bio}</p>
            {p.quote_card && (
              <div className="pf2-quotecard" style={{ marginTop: 10 }}>
                <span className="q">“</span>
                <p>« {p.quote_card.text} »</p>
                <div className="sig">{p.quote_card.author}</div>
              </div>
            )}
            {p.stats?.length > 0 && (
              <div className="pi-stats" data-testid="pi-stats">
                {p.stats.map((s) => (
                  <div className="pi-stat" key={s.label}><span>{s.label}</span><b>{s.value}</b><small>{s.sub}</small></div>
                ))}
              </div>
            )}
          </div>
          <div>
            {p.side_info?.length > 0 && (
              <div className="pi-side" data-testid="pi-side-info">
                {p.side_info.map((s) => {
                  const SI = SIDE_ICONS[s.icon] || CalendarDays;
                  return (
                    <div className="pi-siderow" key={s.label}>
                      <span className="ic"><SI size={14} /></span>
                      <div><span className="l">{s.label}</span><b>{s.value}</b>{s.sub && <small>{s.sub}</small>}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {p.fondation && (
              <button className="pi-fondation" data-testid="pi-fondation" onClick={openFiche}>
                <div className="hd"><b>{p.fondation.name}</b><ArrowRight size={14} color="#F0590B" /></div>
                <span className="sb">{p.fondation.sub}</span>
                {p.fondation.points.map((pt) => <span className="pt" key={pt}>✅ {pt}</span>)}
              </button>
            )}
          </div>
        </div>

        {p.news?.length > 0 && (
          <>
            <div className="pr-sechead"><h3><FileText size={16} color="#F0590B" /> Actualités récentes</h3><button onClick={openFiche}>Voir tout <ArrowRight size={12} /></button></div>
            <div className="pr-row" data-testid="pi-news">{p.news.map((n) => (
              <div key={n.title} className="pf2-news" onClick={openFiche} style={{ cursor: "pointer" }}>
                <img src={n.image} alt="" />
                <span className="d">{new Date(n.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                <b>{n.title}</b><span className="s">{n.source}</span>
              </div>
            ))}</div>
          </>
        )}
        <div className="pi-mediagrid">
          {p.photos?.length > 0 && (
            <div>
              <div className="pr-sechead"><h3><Camera size={15} color="#F0590B" /> Photos</h3><button onClick={openFiche}>Voir tout <ArrowRight size={12} /></button></div>
              <div className="pr-row">{p.photos.slice(0, 4).map((ph) => <img key={ph} src={ph} alt="" className="pi-ph" onClick={openFiche} />)}</div>
            </div>
          )}
          {p.videos?.length > 0 && (
            <div>
              <div className="pr-sechead"><h3><PlayCircle size={15} color="#F0590B" /> Vidéos</h3><button onClick={openFiche}>Voir tout <ArrowRight size={12} /></button></div>
              <div className="pr-row">{p.videos.slice(0, 3).map((v) => (
                <div key={v.title} className="pf2-vid" onClick={openFiche} style={{ cursor: "pointer" }}>
                  <div className="th"><img src={v.image} alt="" /><span className="pl"><PlayCircle size={20} color="#fff" /></span>{v.duration && <span className="dur">{v.duration}</span>}</div>
                  <b>{v.title}</b>
                </div>
              ))}</div>
            </div>
          )}
        </div>
        {p.associates?.length > 0 && (
          <>
            <div className="pr-sechead"><h3><Camera size={15} color="#F0590B" /> Personnalités et institutions associées</h3><button onClick={openFiche}>Voir plus <ArrowRight size={12} /></button></div>
            <div className="pr-row" data-testid="pi-associates">{p.associates.map((a) => (
              <button key={a.name} className="pi-assoc" onClick={() => (a.slug ? navigate(`/personnalite/${a.slug}`) : openFiche())}>
                <img src={a.image} alt="" />
                <b>{a.name}</b><span>{a.meta}</span>
              </button>
            ))}</div>
          </>
        )}
      </div>
    );
  }

  if (p.role_type === "artist") {
    return (
      <div data-testid={`personality-artist-${p.slug}`}>
        <div className="pr-card">
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div className="pa-portrait" style={{ width: 84, height: 84, flexShrink: 0 }}>
              <img src={p.portrait} alt={p.name} />
              <span className="pl"><PlayCircle size={18} color="#fff" /></span>
            </div>
            <div style={{ minWidth: 150, flex: 1 }}>
              <span className="pr-badge orange"><Mic2 size={11} /> {p.org_badge}</span>
              <div className="pr-name" style={{ fontFamily: "Georgia, serif", fontSize: 17, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name} {p.verified && <BadgeCheck size={15} color="#2563EB" fill="#DBEAFE" />}</div>
              <div className="pr-sub" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title}</div>
              {p.rating && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 11.5, fontWeight: 900, color: "var(--ah-title)" }}><Star size={12} color="#F59E0B" fill="#F59E0B" /> {String(p.rating).replace(".", ",")} ({p.reviews_count.toLocaleString("fr-FR")}) <span style={{ color: "#10B981", fontSize: 10.5 }}>● {p.open_label}</span></div>}
            </div>
            <button className="pr-cta" style={{ marginTop: 0, flexShrink: 0, fontSize: 12, padding: "9px 14px" }} onClick={openFiche} data-testid={`personality-fiche-btn-${p.slug}`}>Voir la fiche <ArrowRight size={14} /></button>
          </div>
          <div className="pr-actions">
            <button className="pr-act" onClick={() => toast.success(`Vous suivez ${p.short_name}`)}><Heart size={14} /> Suivre</button>
            <button className="pr-act" onClick={() => share(p.name, url())}><Share2 size={14} /> Partager</button>
            <button className="pr-act" onClick={() => toast.success("Enregistré")}><Bookmark size={14} /> Enregistrer</button>
            <button className="pr-act" onClick={() => toast.info("Écoute — démonstration")}>🎧 Écouter</button>
            <button className="pr-act" onClick={openFiche}><CalendarDays size={14} /> Voir les événements</button>
          </div>
        </div>
        {p.music?.length > 0 && (
          <>
            <div className="pr-sechead"><h3><Music2 size={16} color="#F0590B" /> Musique populaire</h3><button onClick={openFiche}>Voir tout <ArrowRight size={12} /></button></div>
            <div className="pr-row">{p.music.map((m) => (
              <button key={m.title} className="pa-track" onClick={openFiche}>
                <div className="cv">{m.image ? <img src={m.image} alt="" /> : <div className="ph" />}<span className="pl"><PlayCircle size={22} color="#fff" /></span></div>
                <b>{m.title}</b><span>{p.short_name}</span>
              </button>
            ))}</div>
          </>
        )}
        {p.events?.length > 0 && (
          <>
            <div className="pr-sechead"><h3><CalendarDays size={16} color="#F0590B" /> Événements à venir</h3><button onClick={openFiche}>Voir tout <ArrowRight size={12} /></button></div>
            <div className="pr-row">{p.events.map((e) => (
              <div key={e.title} className="pa-event">
                <div className="im">{e.image && <img src={e.image} alt="" />}<span className="date">{e.day}<small>{e.month}</small></span><Bookmark size={13} color="#fff" style={{ position: "absolute", top: 8, right: 8 }} /></div>
                <b>{e.title}</b>
                <span><MapPin size={9} style={{ verticalAlign: -1 }} /> {e.place}</span>
                <button className="tk" onClick={() => toast.info("Billetterie — démonstration")}>🎟 Voir les tickets</button>
              </div>
            ))}</div>
          </>
        )}
        {p.news?.length > 0 && (
          <>
            <div className="pr-sechead"><h3><FileText size={16} color="#F0590B" /> Actualités récentes</h3><button onClick={openFiche}>Voir tout <ArrowRight size={12} /></button></div>
            <div className="pr-row">{p.news.map((n) => (
              <div key={n.title} className="pf2-news" onClick={openFiche} style={{ cursor: "pointer" }}>
                <img src={n.image} alt="" />
                <span className="d">{new Date(n.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                <b>{n.title}</b><span className="s">{n.source}</span>
              </div>
            ))}</div>
          </>
        )}
        {p.related?.length > 0 && (
          <>
            <div className="pr-sechead"><h3><Users size={15} color="#F0590B" /> Personnes et artistes associés</h3><button onClick={openFiche}>Voir plus <ArrowRight size={12} /></button></div>
            <div className="pr-row">{p.related.map((r) => (
              <div key={r.name} className="pa-rel">
                <img src={r.image || "/aya/people/placeholder-person.jpg"} alt="" />
                <b>{r.name}</b>
              </div>
            ))}</div>
          </>
        )}
      </div>
    );
  }

  // Modèle STANDARD (Réf A — Maître Allegra) : toutes les autres personnalités.
  return (
    <div data-testid={`personality-standard-${p.slug}`}>
      <div className="pr-card">
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <img src={p.cover} alt={p.org || p.name} className="pr-img" style={{ width: 84, height: 84, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 150 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {p.org_badge && <span className="pr-badge">🏛 {p.org_badge}</span>}
              {p.verified && <span className="pr-badge verified"><BadgeCheck size={11} /> VÉRIFIÉ</span>}
            </div>
            <div className="pr-name" style={{ fontSize: 15.5, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.org ? `${p.org} — ${p.name}` : p.name}</div>
            <div className="pr-sub" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title} · <MapPin size={11} style={{ verticalAlign: -1 }} /> {p.location}</div>
            {p.rating && <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 4, fontSize: 12, fontWeight: 900, color: "var(--ah-title)" }}><Star size={12} color="#F59E0B" fill="#F59E0B" /> {String(p.rating).replace(".", ",")} ({p.reviews_count}) {p.open_label && <span className="pr-chip" style={{ fontSize: 10 }}>🕐 {p.open_label}</span>}</div>}
          </div>
          <button className="pr-cta" style={{ marginTop: 0, flexShrink: 0, fontSize: 12, padding: "9px 14px" }} onClick={openFiche} data-testid={`personality-fiche-btn-${p.slug}`}>Entrer dans le cabinet <ArrowRight size={14} /></button>
        </div>
        <div className="pr-actions">
          <button className="pr-act" onClick={() => toast.info("Coordonnées publiques dans la fiche")}><Phone size={13} /> Appeler</button>
          <button className="pr-act" onClick={() => toast.info("Itinéraire — bientôt")}>🧭 Itinéraire</button>
          <button className="pr-act" onClick={() => share(p.name, url())}><Share2 size={13} /> Partager</button>
        </div>
      </div>
      {p.services?.length > 0 && (
        <>
          <div className="pr-sechead"><h3>Services et domaines</h3><button onClick={openFiche}>Voir plus <ArrowRight size={12} /></button></div>
          <div className="pr-row">{p.services.map((s) => (
            <button key={s.label} className="pr-tile" onClick={openFiche}><img src={s.image} alt="" /><span>{s.label}</span></button>
          ))}</div>
        </>
      )}
      <div className="pr-sechead"><h3>Fiche personnalité de {p.short_name}</h3><button onClick={openFiche} data-testid={`personality-voirfiche-${p.slug}`}>Voir la fiche <ArrowRight size={12} /></button></div>
      <div className="pr-card" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <img src={p.portrait} alt={p.name} className="pr-img" style={{ width: 84, height: 84, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="pr-name" style={{ fontSize: 15 }}>{p.name} {p.verified && <BadgeCheck size={15} color="#2563EB" fill="#DBEAFE" />}</div>
          <div className="pr-sub" style={{ color: "var(--ah-accent)", fontWeight: 800 }}>{p.title.split("·")[0]} · Fondateur {p.org ? `du ${p.org}` : ""}</div>
          <div style={{ display: "flex", gap: 13, marginTop: 8, flexWrap: "wrap" }}>
            {[[GraduationCap, "Parcours"], [FileText, "Formation"], [Briefcase, "Expériences"], [HandHeart, "Engagements"], [Camera, "Photos"], [PlayCircle, "Vidéos"], [Mic2, "Interventions"], [BookOpen, "Publications"]].map(([I, l]) => (
              <button key={l} onClick={openFiche} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, fontFamily: "inherit" }}>
                <I size={16} color="#2563EB" /><span style={{ fontSize: 9, fontWeight: 800, color: "var(--ah-text)" }}>{l}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {p.related?.length > 0 && (
        <>
          <div className="pr-sechead"><h3>Recherches associées</h3><button onClick={openFiche}>Voir plus <ArrowRight size={12} /></button></div>
          <div className="pr-row">{p.related.map((r) => (
            <div key={r.name} className="pr-event" style={{ flex: "0 0 150px" }}><div className="b"><b>{r.name}</b><span>{r.meta}</span></div></div>
          ))}</div>
        </>
      )}
    </div>
  );
}

// ── Familles de résultats (Réf F — Coco) ────────────────────────────────
export function SearchFamilies({ families }) {
  const navigate = useNavigate();
  if (!families?.list?.length) return null;
  return (
    <div data-testid="search-families">
      {families.list.map((f) => (
        <div key={f.id} data-testid={`family-${f.id}`}>
          <div className="pr-sechead">
            <h3>
              <span style={{ width: 30, height: 30, borderRadius: "50%", background: { knowledge: "#0E8A4F", images: "#F0590B", commerce: "#F0590B", places: "#2563EB" }[f.id], color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {{ knowledge: <BookOpen size={15} />, images: <ImgIcon size={15} />, commerce: "🛒", places: <MapPin size={15} /> }[f.id]}
              </span>
              {f.title}
            </h3>
            <button onClick={() => f.id === "commerce" && f.items?.[0] ? navigate(f.items[0].link) : toast.info("Plus de résultats bientôt")}>Voir plus <ArrowRight size={12} /></button>
          </div>
          {f.subtitle && <div className="pr-sub" style={{ margin: "-4px 0 8px" }}>{f.subtitle}</div>}
          {f.id === "knowledge" && (
            <div className="pr-know" data-testid="family-knowledge-card">
              <img src={f.data.image} alt="" className="pr-know-img" />
              <div className="pr-know-body">
                <b>{f.data.title}</b>
                <p>{f.data.text}</p>
                <div className="pr-chiprow pr-chiprow-compact">{f.data.chips.map((c) => <span key={c} className="pr-chip">{c}</span>)}</div>
              </div>
            </div>
          )}
          {f.id === "images" && (
            <div className="pr-row" data-testid="family-images-row">
              {f.items.map((im) => <div key={im.label} className="pr-tile pr-imgtile"><img src={im.src} alt={im.label} /><span>{im.label}</span></div>)}
            </div>
          )}
          {f.id === "commerce" && (
            <div className="pr-row">
              {f.items.map((it, i) => (
                <button key={i} className="pr-tile" style={{ flex: "0 0 150px", textAlign: "left" }} onClick={() => navigate(it.link)} data-testid={`commerce-item-${i}`}>
                  {it.image && <img src={it.image} alt="" style={{ width: 150, height: 110 }} />}
                  <span style={{ fontSize: 12, fontWeight: 900 }}>{it.name}</span>
                  {it.sub && <span style={{ color: "var(--ah-soft)", fontSize: 10.5 }}>{it.sub}</span>}
                  <span style={{ color: "#F0590B", fontWeight: 900, fontSize: 13, marginTop: 2 }}>{fcfa(it.price)}</span>
                  <span style={{ color: "var(--ah-muted)" }}>{it.shop_name}{it.rating ? ` · ★ ${String(it.rating).replace(".", ",")} (${it.reviews_count})` : ""}</span>
                  <span style={{ color: "var(--ah-muted)" }}><MapPin size={9} style={{ verticalAlign: -1 }} /> {it.city}</span>
                </button>
              ))}
            </div>
          )}
          {f.id === "places" && (
            <div className="pr-row">
              {f.items.map((it, i) => (
                <button key={i} className="pr-tile" style={{ flex: "0 0 150px", textAlign: "left" }} onClick={() => it.link && navigate(it.link)} data-testid={`place-item-${i}`}>
                  {it.image && <img src={it.image} alt="" style={{ width: 150, height: 100 }} />}
                  <span style={{ fontSize: 12, fontWeight: 900 }}>{it.name}</span>
                  <span style={{ color: "var(--ah-muted)" }}>{it.rating ? `★ ${String(it.rating).replace(".", ",")} · ` : ""}{it.category}{it.commune ? ` · ${it.commune}` : ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
